import type { ReactNode } from 'react';
import type { LineSlug } from './lines';

/**
 * How far along the platform the rider is, as a little piece of each line's
 * world: a sakura wreath on the Sakura line, a tachometer on the Rapid line, a
 * ring of candles on the Dread line. One mark per ticket. The current one
 * burns brightest, the ones already looked at stay lit, the rest are dark.
 *
 * A platform can hold a single ticket or twenty. Spreading one to three marks
 * evenly round a ring left a lonely petal over a big empty circle, so a short
 * platform keeps the ornament whole: its tickets sit together around twelve
 * o'clock and the positions left over are drawn as the ornament's quiet parts
 * (hour ticks, tracery, leaves on the branch).
 *
 * It is a gauge, not a control: the stubs beside it and the arrow keys do the
 * moving, so it is hidden from assistive tech and takes no pointer events.
 */

type State = 'on' | 'seen' | 'off';

interface Geo {
  /** Angle of ticket i in degrees, clockwise from 12 o'clock. */
  at: (i: number) => number;
  /** Degrees between neighbouring positions. */
  step: number;
  n: number;
  cur: number;
}

interface Motif {
  /** Degrees the marks spread over; 360 is a full ring. */
  sweep: number;
  /** Fewest positions on a full ring. Shorter platforms fill the rest with `blank`. */
  min?: number;
  /** Behind the marks. */
  back?: (g: Geo) => ReactNode;
  /** One ticket's mark, drawn pointing up; the dial rotates it into place. */
  seg: (state: State, g: Geo) => ReactNode;
  /** A position no ticket uses, drawn pointing up; k counts them from 0. */
  blank?: (g: Geo, k: number) => ReactNode;
  /** Above the marks, under the hub. */
  front?: (g: Geo) => ReactNode;
  /** Rotate the whole ring so the current mark sits at the top. */
  turn?: boolean;
}

const rad = (deg: number) => (deg * Math.PI) / 180;
const pt = (r: number, deg: number) => [r * Math.sin(rad(deg)), -r * Math.cos(rad(deg))] as const;
const f = (n: number) => +n.toFixed(2);

/** Half-width a mark can have at radius r without touching its neighbours. */
const room = (g: Geo, r: number, max: number) => Math.min(max, r * Math.sin(rad(Math.min(g.step, 120)) / 2) * 0.86);

function annulus(r0: number, r1: number, a0: number, a1: number) {
  const [x0, y0] = pt(r1, a0);
  const [x1, y1] = pt(r1, a1);
  const [x2, y2] = pt(r0, a1);
  const [x3, y3] = pt(r0, a0);
  const big = a1 - a0 > 180 ? 1 : 0;
  return `M${f(x0)},${f(y0)}A${r1},${r1} 0 ${big} 1 ${f(x1)},${f(y1)}L${f(x2)},${f(y2)}A${r0},${r0} 0 ${big} 0 ${f(x3)},${f(y3)}Z`;
}

const arc = (r: number, a0: number, a1: number) => {
  const [x0, y0] = pt(r, a0);
  const [x1, y1] = pt(r, a1);
  return `M${f(x0)},${f(y0)}A${r},${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${f(x1)},${f(y1)}`;
};

/** A pointed oval from r0 outwards: a rose-window light. */
const lancet = (r0: number, len: number, w: number) =>
  `M0,${-r0}Q${f(w)},${f(-(r0 + len / 2))} 0,${f(-(r0 + len))}Q${f(-w)},${f(-(r0 + len / 2))} 0,${-r0}Z`;

/** A cherry petal from the flower's centre: round shoulders and the notch at the tip. */
const petal = (len: number, w: number) =>
  `M0,0C${f(w * 1.1)},${f(-len * 0.25)} ${f(w)},${f(-len)} ${f(w * 0.36)},${f(-len)}` +
  `L0,${f(-len * 0.8)}L${f(-w * 0.36)},${f(-len)}` +
  `C${f(-w)},${f(-len)} ${f(-w * 1.1)},${f(-len * 0.25)} 0,0Z`;

/** A five-petalled blossom centred on (0, -cy). */
const blossom = (cy: number, r: number, cls: string) => (
  <g className={cls} transform={`translate(0 ${-cy})`}>
    {[0, 72, 144, 216, 288].map((a) => (
      <path key={a} className="petal" d={petal(r, r * 0.56)} transform={`rotate(${a})`} />
    ))}
    <circle className="eye" r={f(r * 0.26)} />
  </g>
);

const flame = (base: number, len: number, w: number) =>
  `M0,${f(-base)}C${f(w)},${f(-(base + len * 0.35))} ${f(w * 0.5)},${f(-(base + len * 0.7))} 0,${f(-(base + len))}` +
  `C${f(-w * 0.5)},${f(-(base + len * 0.7))} ${f(-w)},${f(-(base + len * 0.35))} 0,${f(-base)}Z`;

/** A hand from the hub to the current mark. */
const hand = (g: Geo, len: number, cls = 'hand') => (
  <g className={cls} style={{ transform: `rotate(${g.at(g.cur)}deg)` }}>
    <path d={`M-1.6,6L0,${-len}L1.6,6Z`} />
  </g>
);

const ring = (r: number, cls: string) => <circle r={r} className={cls} />;

/** A short radial tick from r0 out to r1. */
const tick = (r0: number, r1: number, w: number, cls = 'blank') => (
  <rect className={cls} x={f(-w / 2)} y={-r1} width={w} height={r1 - r0} rx={f(w / 2)} />
);

function gear(teeth: number, r0: number, r1: number) {
  let d = '';
  const step = 360 / teeth;
  for (let i = 0; i < teeth; i++) {
    const a = i * step;
    const pts = [pt(r0, a - step * 0.5), pt(r0, a - step * 0.28), pt(r1, a - step * 0.18), pt(r1, a + step * 0.18), pt(r0, a + step * 0.28)];
    d += pts.map(([x, y], k) => `${i === 0 && k === 0 ? 'M' : 'L'}${f(x)},${f(y)}`).join('');
  }
  return d + 'Z';
}

const MOTIFS: Record<LineSlug, Motif> = {
  // 桜線: a sakura wreath. Each ticket is a bud on the branch that opens once
  // it has been looked at; the current one is in full bloom.
  romance: {
    sweep: 360,
    min: 8,
    back: () => (
      <>
        {ring(47, 'rim thin')}
        <circle r={34} className="branch" />
      </>
    ),
    seg: (s, g) => {
      const r = room(g, 34, 11.5);
      if (s === 'off') {
        return (
          <g className="seg off bud" transform="translate(0 -34)">
            <path className="petal" d={petal(r * 0.8, r * 0.42)} />
            <path className="sepal" d={`M${f(-r * 0.3)},${f(-r * 0.2)}Q0,${f(r * 0.32)} ${f(r * 0.3)},${f(-r * 0.2)}Z`} />
          </g>
        );
      }
      return blossom(34, s === 'on' ? r : r * 0.88, `seg ${s}`);
    },
    // one leaf, leaning the way the branch grows, outside and inside in turn
    blank: (g, k) => {
      const w = Math.min(5, room(g, 34, 5));
      return (
        <ellipse className="leaf" rx={f(w * 0.42)} ry={f(w)}
          transform={`translate(0 -34) rotate(${k % 2 ? 118 : 62}) translate(0 ${f(-w * 0.9)})`} />
      );
    },
    front: () => <circle r={18.5} className="hub-ring" />,
  },
  // 疾風線: a rev counter; the needle is the current ticket.
  action: {
    sweep: 260,
    back: () => (
      <>
        {ring(46, 'rim')}
        <path className="track" d={arc(38, -130, 130)} />
        {Array.from({ length: 27 }, (_, i) => (
          <g key={i} transform={`rotate(${-130 + i * 10})`}>
            {tick(39.5, i % 3 === 0 ? 44 : 42, i % 3 === 0 ? 1.4 : 0.8, 'minor')}
          </g>
        ))}
        <path className="redline" d={annulus(41, 45, 100, 130)} />
      </>
    ),
    seg: (s, g) => {
      const half = Math.min(g.step * 0.38, 9);
      return <path className={`seg ${s}`} d={annulus(28, 37, -half, half)} />;
    },
    front: (g) => hand(g, 34, 'hand needle'),
  },
  // 幻想線: a rose window; empty lights are bare tracery.
  fantasy: {
    sweep: 360,
    min: 8,
    back: (g) => (
      <>
        {ring(47, 'rim')}
        {ring(42.5, 'rim thin')}
        {Array.from({ length: Math.round(360 / g.step) }, (_, i) => {
          const [x, y] = pt(33, g.at(0) + (i + 0.5) * g.step);
          return <circle key={i} cx={f(x)} cy={f(y)} r={f(Math.min(4.6, room(g, 33, 4.6)))} className="jewel" />;
        })}
      </>
    ),
    seg: (s, g) => <path className={`seg ${s}`} d={lancet(19, 23, room(g, 30, 9.5))} />,
    blank: (g) => <path className="blank" d={lancet(19, 23, room(g, 30, 9.5))} />,
    front: () => <circle r={18.5} className="hub-ring" />,
  },
  // 爆笑線: a starburst; a ray per ticket, stubby ones round the rest.
  comedy: {
    sweep: 360,
    min: 10,
    back: () => ring(46, 'rim'),
    seg: (s, g) => {
      const w = room(g, 20, 9);
      return <path className={`seg ${s}`} d={`M${f(-w)},-19L0,-46L${f(w)},-19Z`} />;
    },
    blank: (g) => {
      const w = room(g, 20, 9) * 0.7;
      return <path className="blank" d={`M${f(-w)},-19L0,-34L${f(w)},-19Z`} />;
    },
  },
  // 日常線: the kitchen clock; the hand points at today.
  'slice-of-life': {
    sweep: 360,
    min: 12,
    back: () => (
      <>
        <circle r={46} className="face" />
        {ring(46, 'rim')}
      </>
    ),
    seg: (s, g) => {
      const w = room(g, 38, 3.4);
      return <rect className={`seg ${s}`} x={f(-w)} y={-43} width={f(w * 2)} height={10} rx={f(Math.min(w, 2))} />;
    },
    blank: () => tick(37, 43, 1.4),
    front: (g) => hand(g, 30),
  },
  // 電脳線: a HUD ring; the inner scanner never stops.
  'sci-fi': {
    sweep: 360,
    min: 6,
    back: () => (
      <>
        {ring(46, 'rim thin')}
        <circle r={22} className="scan" />
      </>
    ),
    seg: (s, g) => {
      const half = Math.max(1, g.step / 2 - 4);
      return <path className={`seg ${s}`} d={annulus(32, 42, -half, half)} />;
    },
    blank: (g) => {
      const half = Math.max(1, g.step / 2 - 4);
      return <path className="blank" d={annulus(34, 40, -half, half)} />;
    },
  },
  // 推理線: a safe dial, turned so the current number sits under the mark.
  mystery: {
    sweep: 360,
    min: 12,
    turn: true,
    back: () => (
      <>
        {Array.from({ length: 48 }, (_, i) => (
          <line key={i} className="knurl" x1={0} y1={-46} x2={0} y2={-42} transform={`rotate(${i * 7.5})`} />
        ))}
      </>
    ),
    seg: (s, g) => {
      const w = room(g, 33, 3);
      return <path className={`seg ${s}`} d={`M${f(-w)},-40L${f(w)},-40L${f(w * 0.6)},-24L${f(-w * 0.6)},-24Z`} />;
    },
    blank: () => tick(33, 40, 1.2),
    front: () => <path className="mark" d="M-4,-50L4,-50L0,-43Z" />,
  },
  // 戦慄線: a ring of candles; unvisited ones are only wicks.
  horror: {
    sweep: 360,
    min: 8,
    back: () => (
      <>
        {ring(46, 'rim')}
        {ring(24, 'rim thin')}
      </>
    ),
    seg: (s, g) => {
      const w = room(g, 26, 4);
      return (
        <g className={`seg candle ${s}`}>
          <rect className="wax" x={f(-w * 0.7)} y={-30} width={f(w * 1.4)} height={12} rx={1} />
          <path className="flame" d={flame(30, 13, w * 0.9)} />
        </g>
      );
    },
    // a candle burnt down to a pool of wax
    blank: (g) => {
      const w = room(g, 26, 4);
      return <ellipse className="blank" cy={-21} rx={f(w * 0.9)} ry={f(w * 0.45)} />;
    },
  },
  // 熱血線: a stopwatch.
  sports: {
    sweep: 360,
    min: 12,
    back: () => (
      <>
        <rect className="crown" x={-5} y={-50} width={10} height={5} rx={1.2} />
        {ring(44, 'rim')}
      </>
    ),
    seg: (s, g) => {
      const half = Math.max(1, Math.min(g.step / 2 - 2.5, 14));
      return <path className={`seg ${s}`} d={annulus(34, 41, -half, half)} />;
    },
    blank: () => tick(36, 41, 1.3),
    front: (g) => hand(g, 32),
  },
  // 異界線: a portal; runes round its edge, rings turning inside.
  isekai: {
    sweep: 360,
    min: 8,
    back: () => (
      <>
        <circle r={44} className="portal" />
        <circle r={31} className="whirl a" />
        <circle r={24} className="whirl b" />
      </>
    ),
    seg: (s, g) => {
      const w = room(g, 38, 5.5);
      return <path className={`seg ${s}`} d={`M0,-46L${f(w)},-38L0,-30L${f(-w)},-38Z`} />;
    },
    blank: () => <path className="blank" d="M0,-41L2.2,-38L0,-35L-2.2,-38Z" />,
  },
  // 機甲線: a gear, with a warning lamp per ticket and bolts between.
  mecha: {
    sweep: 360,
    min: 8,
    back: () => (
      <>
        <path className="gear" d={gear(12, 40, 47)} />
        {ring(36, 'rim thin')}
      </>
    ),
    seg: (s, g) => <circle className={`seg ${s}`} cy={-29} r={f(room(g, 29, 4.6))} />,
    blank: () => (
      <g className="bolt" transform="translate(0 -29)">
        <circle r={2.4} />
        <path d="M-1.4,0H1.4" />
      </g>
    ),
  },
  // 響線: a record; the equaliser round it jumps for what you have heard.
  music: {
    sweep: 360,
    min: 12,
    back: () => (
      <>
        <circle r={28} className="vinyl" />
        {ring(24, 'groove')}
        {ring(20, 'groove')}
      </>
    ),
    seg: (s, g) => {
      const w = room(g, 32, 3.2);
      const len = s === 'on' ? 17 : s === 'seen' ? 11 : 5;
      return <rect className={`seg ${s}`} x={f(-w)} y={-(31 + len)} width={f(w * 2)} height={len} rx={1} />;
    },
    blank: (g) => {
      const w = room(g, 32, 3.2);
      return <rect className="blank" x={f(-w)} y={-33.5} width={f(w * 2)} height={2.5} rx={1} />;
    },
  },
  // 妖線: paper lanterns hung in a ring; bare hooks where there are none.
  supernatural: {
    sweep: 360,
    min: 8,
    back: () => ring(46, 'rim thin'),
    seg: (s, g) => {
      const w = room(g, 33, 6);
      return (
        <g className={`seg lantern ${s}`}>
          <line x1={0} x2={0} y1={-46} y2={-43} className="cord" />
          <rect x={f(-w)} y={-43} width={f(w * 2)} height={17} rx={f(w * 0.9)} />
          <line x1={f(-w)} x2={f(w)} y1={-38} y2={-38} className="band" />
          <line x1={f(-w)} x2={f(w)} y1={-31} y2={-31} className="band" />
        </g>
      );
    },
    blank: () => (
      <g className="hook">
        <line x1={0} x2={0} y1={-46} y2={-41} />
        <circle cy={-40} r={1.1} />
      </g>
    ),
  },
};

/**
 * Where ticket i sits. A full ring with fewer tickets than `min` keeps `min`
 * positions and centres the tickets on 12 o'clock; a sweep (the tachometer)
 * spreads them over its arc, a lone ticket straight up.
 */
function layout(m: Motif, n: number) {
  if (m.sweep < 360) {
    const pad = 16;
    const span = m.sweep - pad * 2;
    const step = n > 1 ? span / (n - 1) : span;
    const start = n > 1 ? -span / 2 : 0;
    return { slots: n, step, at: (i: number) => start + i * step };
  }
  const slots = Math.max(n, m.min ?? n);
  const step = 360 / slots;
  const start = n < slots ? (-(n - 1) * step) / 2 : 0;
  return { slots, step, at: (i: number) => start + i * step };
}

export function Dial({ slug, n, cur, seen }: { slug: LineSlug; n: number; cur: number; seen: Set<number> }) {
  const m = MOTIFS[slug];
  const { slots, step, at } = layout(m, n);
  const g: Geo = { at, step, n, cur };
  const state = (i: number): State => (i === cur ? 'on' : seen.has(i) ? 'seen' : 'off');
  const turn = m.turn ? { transform: `rotate(${-g.at(cur)}deg)` } : undefined;

  return (
    <div className={`dial m-${slug}`} aria-hidden="true">
      <svg viewBox="-50 -50 100 100">
        <g className="spin" style={turn}>
          {m.back?.(g)}
          {m.blank &&
            Array.from({ length: slots - n }, (_, k) => (
              <g key={`b${k}`} transform={`rotate(${f(g.at(n + k))})`}>
                {m.blank!(g, k)}
              </g>
            ))}
          {Array.from({ length: n }, (_, i) => (
            <g key={i} transform={`rotate(${f(g.at(i))})`}>
              {m.seg(state(i), g)}
            </g>
          ))}
        </g>
        {m.front?.(g)}
        <circle r={16} className="hub" />
        <text className="num" y={n > 1 ? 3.5 : 5.5}>
          {cur + 1}
        </text>
        {n > 1 && (
          <text className="of" y={11}>
            /{n}
          </text>
        )}
      </svg>
    </div>
  );
}
